import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { BaseCrud } from 'react/containers/layout/Crud';

import { getResourceTo } from 'react/utils/utils';
import { getIntegration, getIntegrationLogs } from 'react/api/selectors/integrations';
import IntegrationFormSection from 'react/components/Integrations/IntegrationFormSection';
import { useIntegrations } from 'react/hooks/integrations.hook';

function RetriedLogsCrud(props) {
  const nameSpace = useMemo(() => props.nameSpace, [props.nameSpace]);

  const {
    getActiveFilters,
    downloadCSVFromData,
    setSortBy,
    setCustomColumnsFormatter,
    setCustomOnClickRow
  } = useIntegrations(nameSpace);

  const { integration } = useSelector((state) => getIntegration(state, props.id)?.toJS() || {});
  const integrationLogs = useSelector((state) => getIntegrationLogs(state, `${props.id}-retriedLogs`));

  const hideLoading = () => {
    App.Forms[nameSpace]?.filterBuilder?.loading?.hide();
  };

  const getParsedCollectionData = (data) => {
    const arrayOfData = data.map((item) => new App.Models.IntegrationLog(item));
    const parsedData = new App.Collections.IntegrationLogs();

    arrayOfData.forEach((item) => {
      parsedData.add(item);
    });

    return parsedData;
  };

  const addDataToGrid = (data) => {
    const columns = App.Forms[nameSpace].table.getColumns();
    const parsedData = getParsedCollectionData(data);

    if (!data.length) {
      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, false);
      Monet.Grid.Utils.showGridNoResult(nameSpace);

      hideLoading();

      return;
    }

    Monet.Grid.Utils.hideGridNoResult(nameSpace);
    Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, false);

    hideLoading();
  };

  const setCustomExecuteFilter = () => {
    App.Forms[nameSpace].filterBuilder.customRequest = () => {
      Monet.Grid.Utils.hideGridNoResult(nameSpace);

      const activeFilters = getActiveFilters();

      let filteredData = integrationLogs?.toJS ? integrationLogs?.toJS() : [];

      Object.entries(activeFilters).forEach(([key, value]) => {
        filteredData = filteredData.filter((item) => {
          const selectedItem = item[key];

          if (key === 'requestDate') {
            const parsedItem = new Date(selectedItem);
            parsedItem.setSeconds(0);

            return parsedItem.getTime() === new Date(value).getTime();
          } else {
            const parsedItem = String(selectedItem).toUpperCase();
            const filterEq = String(value).toUpperCase();

            return parsedItem == filterEq;
          }

        });
      });

      addDataToGrid(filteredData);
    };
  };

  const setCustomSaveList = () => {
    const {
      actions: { exportFormats },
      fields: formFields
    } = App.Forms[nameSpace];

    App.Forms[nameSpace].actions.exportFormats.saveFieldList = () => {
      exportFormats.handlerToggleLoadingInBtn();

      const fields = exportFormats.getFieldsSelecteds();

      const date = new Date();
      const fileName = `RetriedLogs_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`;

      downloadCSVFromData({
        formFields,
        fields,
        fileName,
        exportFormats
      });
    };
  };

  const setCustomFilterBuilderFields = () => {
    let fieldsList = App.Forms[nameSpace]?.filterBuilder?.fieldList;

    delete fieldsList.httpStatus_Range;
    delete fieldsList.responseBody;
    delete fieldsList.requestDate_Range;
    delete fieldsList.requestBody;

    fieldsList = Object.keys(fieldsList);

    App.Forms[nameSpace]?.filterBuilder?.setFieldsToDisplayOnFilterBuilder(fieldsList);
    App.Forms[nameSpace]?.filterBuilder?.render();
  };

  const setCustomCustomizeColumns = () => {
    App.Forms[nameSpace].actions.customizecolumns.setCallBacks({
      afterSaveList: () => {
        const router = App.Objects[nameSpace];

        router.list();
      }
    });
  };

  const removeSaveGridLayoutButton = () => {
    const saveGridButton = document.querySelector(`#${props.nameSpace} .saveLayout.gridActionDiv`);

    saveGridButton && saveGridButton.remove();
  };

  const setDataProvider = () => {
    const dataProvider = App.Forms[nameSpace]?.dataProvider?.data || [];
    const integrationLogsInState = GreenmileReduxStore
      .getState()
      .integrations
      .getIn(['integrationsLogsPage', `${props.id}-retriedLogs`, 'logs'], []);

    if (!dataProvider?.length && integrationLogsInState?.size) {
      const parsedData = getParsedCollectionData(integrationLogsInState.toJS());
      const columns = App.Forms[nameSpace].table.getColumns();

      Monet.Grid.addDataToGrid(nameSpace, columns, parsedData, true);
    }
  };

  useEffect(() => {
    App.Objects[nameSpace].afterPopulateGrid = () => {
      setCustomExecuteFilter();
      setCustomSaveList();
      setCustomCustomizeColumns();
      removeSaveGridLayoutButton();
      setDataProvider();
      setSortBy();
      setCustomFilterBuilderFields();
      setCustomColumnsFormatter();
      setCustomOnClickRow();
    };
  }, []);

  useEffect(() => {
    if (integrationLogs?.size >= 0 && App.Forms[nameSpace]?.table) {
      const data = integrationLogs.toJS();

      addDataToGrid(data);
    }

    App.Forms[nameSpace]?.table && setCustomExecuteFilter();
  }, [integrationLogs]);

  useEffect(() => {
    const { updateTitle, id } = props;

    updateTitle({
      key: `RetriedLogs_${id}`,
      title: getResourceTo('RetriedLogs.screen.tabName', integration?.name)
    });
  }, [integration?.name]);

  return (
    <BaseCrud
      entity={`IntegrationLog_${props.id}`}
      updateMethod="PATCH"
      tabName={getResourceTo('RetriedLogs.screen.title', integration?.name)}
      customOptions={{
        rowHeight: 32
      }}
      customHeaderDescription={getResourceTo('IntegrationLog.screen.description')}
      {...props}
    >
      <IntegrationFormSection title='retried.section.general.title' />
    </BaseCrud>
  );
}

RetriedLogsCrud.propTypes = {
  id: PropTypes.string.isRequired,
  updateTitle: PropTypes.func.isRequired
};

export default RetriedLogsCrud;
